/**
 * Payments (§13.6).
 *
 * Every deduction is its own line. A vendor who sees only the net figure
 * assumes they were short-paid, and that phone call costs more than the
 * deduction did — so commission and TDS are shown with names, not folded into
 * a total labelled "adjustments".
 */
import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type Payout, type PayoutsResponse } from '../lib/api';
import { useI18n, useT, type StringKey } from '../lib/i18n';
import { formatINR, shortDate } from '../lib/format';
import {
  Badge,
  Card,
  EmptyState,
  ErrorNote,
  Meta,
  MoneyRow,
  PageHero,
  SectionTitle,
  Spinner,
  type Tone,
} from '../components/Primitives';
import { Icon } from '../components/Icon';

const STATUS_TONE: Record<string, Tone> = {
  pending: 'neutral',
  approved: 'good',
  paid: 'good',
  held: 'warn',
  disputed: 'bad',
};

export function PayoutsPage() {
  const { t } = useI18n();
  const T = useT();
  const [data, setData] = useState<PayoutsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.get<PayoutsResponse>('/v1/partner/payouts'));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.somethingWrong'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !data) return <Spinner />;
  if (error) return <ErrorNote message={error} onRetry={() => void load()} />;
  const payouts = data?.payouts ?? [];
  const paid = payouts
    .filter((p) => p.status === 'paid')
    .reduce((sum, p) => sum + BigInt(p.netMicros), 0n);
  const hero = (
    <PageHero
      icon="payouts"
      eyebrow={t('payouts.pending')}
      title={formatINR(data?.pendingNetMicros ?? '0')}
      sub={T(
        'हर कटौती अलग लिखी है — कमीशन और TDS, साफ़-साफ़।',
        'Every deduction on its own line — commission and TDS, in plain sight.',
      )}
      stats={[
        { label: T('मिल चुका', 'Paid so far'), value: formatINR(paid) },
        { label: T('पेमेंट', 'Payments'), value: String(payouts.length) },
      ]}
    />
  );
  if (payouts.length === 0)
    return (
      <>
        {hero}
        <EmptyState icon="payouts" title={t('payouts.empty')} />
      </>
    );

  return (
    <div className="space-y-4">
      {hero}
      <SectionTitle count={payouts.length}>{t('payouts.title')}</SectionTitle>
      <ul className="space-y-3">
        {payouts.map((payout) => (
          <li key={payout.id}>
            <PayoutCard payout={payout} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function PayoutCard({ payout }: { payout: Payout }) {
  const { t, lang } = useI18n();
  const statusKey = `payouts.status.${payout.status}` as StringKey;

  return (
    <Card>
      <div className="flex items-start gap-3">
        <span className="icon-tile h-11 w-11">
          <Icon name={payout.status === 'paid' ? 'check' : 'payouts'} className="h-6 w-6" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-[17px] font-bold leading-snug">
            {payout.jobTitle ?? t('payouts.title')}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <Badge tone={STATUS_TONE[payout.status] ?? 'neutral'}>{t(statusKey)}</Badge>
            {payout.dueOn && (
              <Meta icon="clock">
                {payout.status === 'paid' ? '' : `${t('payouts.due')} `}
                {shortDate(payout.dueOn, lang)}
              </Meta>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3 divide-y divide-line rounded-2xl bg-panel-2 px-4 py-1">
        <MoneyRow label={t('payouts.gross')} value={formatINR(payout.grossMicros)} muted />
        <MoneyRow
          label={t('payouts.commission')}
          value={`− ${formatINR(payout.commissionMicros)}`}
          muted
        />
        <MoneyRow label={t('payouts.tds')} value={`− ${formatINR(payout.tdsMicros)}`} muted />
        <MoneyRow label={t('payouts.net')} value={formatINR(payout.netMicros)} strong />
      </div>

      {payout.holdReason && (
        <div className="mt-3 rounded-2xl bg-amber-50 p-3">
          <p className="text-xs font-semibold text-live">{t('payouts.holdReason')}</p>
          <p className="mt-0.5 text-sm">{payout.holdReason}</p>
        </div>
      )}

      {payout.reference && (
        <p className="tnum mt-3 text-xs text-muted">
          {t('payouts.reference')} {payout.reference}
        </p>
      )}
    </Card>
  );
}
